import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import AuthenticationService from "service/auth/AuthenticationService";
import { DOC_TOKEN_STORAGE_KEY } from "../helper/documentConstants";

/**
 * Patient record + preferred languages.
 *
 * Note these are on baseAPI (api-user-service), not serviceAPI — the patient
 * lives in the monolith while appointments live in the service.
 */

/**
 * Preferred-language editing is ON, at parity with bridge.
 *
 * THE BACKEND BUG IS FIXED (product, 2026-09-10). `POST /patients/{id}/ppl`
 * now REPLACES the stored set with the codes it is sent. It used to ignore the
 * payload and double the existing rows instead — verified on dev1 against
 * patient 18360, where [English, Hindi, Tamil] dropped Tamil and took the rows
 * 2 -> 4, then [English, Hindi] took them 4 -> 8 — and because POST is the only
 * verb the route accepts (PUT / PATCH / DELETE all 405) no client could undo it.
 * That history is why the two habits below are NOT tidying:
 *
 *   · READS ARE STILL DEDUPED. Rows written during the broken period are still
 *     in the table (one patient reached ~19,933 rows for three distinct
 *     languages), so the popover collapses on code AND name. Bridge's fixed
 *     Language.js dedupes on read for the same reason — see the comment on its
 *     getLanguage. A replace-save is now what actually cleans a patient up.
 *   · THE SAVE SENDS A BOUNDED, DEDUPED SET, never the raw stored list. Echoing
 *     the stored rows back is what made bridge's old body grow 3k -> 6k -> 12k
 *     against the doubling backend.
 *
 * The switch is kept as a kill switch rather than deleted: it sits at the
 * network boundary, so setting it to `false` returns the popover to view-only
 * and makes it impossible for any caller to reach the endpoint, whatever else
 * changes. NOT re-verified against the fixed endpoint from here — doing so means
 * writing to a real patient's record, which is the user's call, not a test's.
 */
export const LANGUAGE_EDITING_ENABLED = true;

class PatientService {
  constructor() {
    this.apiService = new APIService();
    /* Constructed ONCE and held: `new AuthenticationService()` re-runs
       setDefaultCookies(), which rewrites the `client` cookie, so building one
       per call would do that on every search keystroke. */
    this.authenticationService = new AuthenticationService();
  }

  /**
   * The patient record: firstName, lastName, gender, age, dateOfBirth,
   * countryCode, phoneno, email, profileType.
   *
   * Not substitutable by the appointment row's denormalised patient* fields:
   * for family bookings the server overwrites patientName/patientGender/
   * patientProfileType with the member's values while leaving patientAge and
   * the phone as the primary's — the row is internally inconsistent.
   */
  fetchPatient(patientId, onResponse, onError) {
    const url = `${APIConfig.base}/patients/${patientId}`;

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Every patient profile on a phone number — the MULTIPROFILE lookup.
   *
   * One phone can carry several people (a parent booking for a child, a shared
   * family handset), which is why this answers a LIST and the caller picks.
   *
   *   GET /patients/search?q={phone}&type=3&paid={paid}&cc={cc}&tenantid={tid}
   *
   * `type=3` is the phone-number search (1 = id, 2 = name); bridge's
   * patients-v2 screen reads the same enum.
   *
   * FANS OUT OVER `paid`. The endpoint filters on it, so a single call returns
   * only half the profiles — bridge queries true AND false and merges, and a
   * booking screen that missed the unpaid half would silently hide a patient.
   * Deduped on patientId because a profile can come back on both legs.
   *
   * A PARTIAL failure is reported, not swallowed: if one leg fails and the
   * other returns nothing, "no profiles on this number" would be a definite
   * answer built on half an answer.
   */
  searchByPhone(phone, countryCode, onResponse, onError) {
    return this._searchProfiles(phone, 3, countryCode, onResponse, onError);
  }

  /**
   * The patient with this ID, in the SAME shape searchByPhone answers.
   *
   *   GET /patients/search?q={id}&type=1&paid={paid}&cc={cc}&tenantid={tid}
   *
   * `type=1` is the id search (api-user-service/api/patient.go:622-636 parses q
   * as an int64 and 400s on anything else).
   *
   * NOT `GET /patients/{id}` — which is what fetchPatient and bridge's booking
   * screen use. That route is backed by `getPatientByWhere("id = ?")` with NO
   * tenant clause and NO status clause (repository/patient.go:58-62), so it
   * would let this screen pull a patient from another tenant, or an inactive
   * one, and start booking against them. type=1 applies exactly the gate the
   * phone route already applies: `id = ? and tenantid = ? and status …`.
   *
   * Answers a list of at most one for symmetry with the phone route, so the
   * caller needs no second code path.
   */
  searchById(patientId, countryCode, onResponse, onError) {
    return this._searchProfiles(patientId, 1, countryCode, onResponse, onError);
  }

  /**
   * The shared engine. `type` picks the search (1 = id, 3 = phone).
   *
   * FANS OUT OVER `paid` for BOTH types. The endpoint filters on it — the
   * backend applies the same paid/free split to type=1 as to type=3
   * (api/patient.go:624-631) — so a single leg returns only half the patients.
   * An id search that skipped this would reintroduce, on a new route, the exact
   * bug scripts/verify/create-appointment-phone.js was written to prevent.
   * Deduped on patientId because a profile can come back on both legs.
   *
   * A PARTIAL failure is reported, not swallowed: if one leg fails and the
   * other returns nothing, "no such patient" would be a definite answer built
   * on half an answer.
   */
  _searchProfiles(query, type, countryCode, onResponse, onError) {
    const q = String(query || "").trim();
    if (!q) {
      onResponse({ profiles: [], partial: false });
      return undefined;
    }
    const cc = countryCode || 91;
    const tid = this.authenticationService.getTenantId() || "";

    const legs = [true, false];
    let pending = legs.length;
    let failures = 0;
    let merged = [];

    const settle = () => {
      pending -= 1;
      if (pending > 0) return;
      if (failures === legs.length) {
        onError(new Error("Couldn't reach the patient service. Please try again."));
        return;
      }
      const seen = {};
      const profiles = merged.filter((p) => {
        const id = p && (p.patientId || p.id);
        if (id == null || seen[id]) return false;
        seen[id] = true;
        return true;
      });
      onResponse({ profiles, partial: failures > 0 });
    };

    legs.forEach((paid) => {
      const url =
        `${APIConfig.base}/patients/search` +
        `?q=${encodeURIComponent(q)}&type=${type}&paid=${paid}` +
        `&cc=${encodeURIComponent(cc)}&tenantid=${encodeURIComponent(tid)}`;
      this.apiService.get(
        url,
        (res) => {
          /* Liberal on the envelope, as bridge is: the endpoint has answered
             a bare array and a {patients:[…]} wrapper at different times. */
          const rows = (res && (res.patients || res.data || res)) || [];
          merged = merged.concat(Array.isArray(rows) ? rows : []);
          settle();
        },
        () => {
          failures += 1;
          settle();
        }
      );
    });
    return undefined;
  }

  /**
   * Save the patient record from the Patient-details dialog (3015:5175) —
   * bridge's updateEmail / updateProfileInfo, PUT /v1/patients/profile/update.
   *
   * The endpoint is a FULL-RECORD REPLACE, not a patch: bridge rebuilds the
   * whole body from its loaded patient every time (PatientProfile.js:205-219),
   * so any field omitted here is cleared server-side. Callers must pass the
   * complete set, which is why buildProfileUpdateBody exists next to it.
   *
   * Two renames the wire insists on, both easy to get wrong:
   *   • the record READS `phoneno` (lower-case o) but WRITES `phoneNo`
   *   • `phoneNo` goes over as a String, and `dateOfBirth` as "YYYY-MM-DD"
   */
  updatePatientProfile(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.updateProfileInfo;

    return this.apiService.put(url, body, onResponse, onError);
  }

  /**
   * The patient's WORK email, for the Patient-details dialog — shown there,
   * never edited (deactivating it is a Bridge Profile action).
   * api-client-config-service GET /v1/account/patient/work-email/patient/{pid}:
   * { workEmail, status (1 active, 2 deactivated), accountCode, … }, or 404
   * "no work email found for patient" when there is none.
   */
  getWorkEmail(patientId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getWorkEmail.replace("{0}", patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Preferred languages — a separate table and a separate call.
   *
   * Returns [{ patientId, code, value }] where `value` is a verbatim copy of
   * `code` (models/patient.go ToResponse), i.e. there is no display label.
   * Deliberately NOT the patient record's singular `preferredLanguage` string:
   * that is a legacy field on a different write path and silently disagrees.
   */
  fetchPatientLanguages(patientId, onResponse, onError) {
    const url = `${APIConfig.base}/patients/${patientId}/ppl`;

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The master language list. Bridge's `language.get` = baseAPI/language, which
   * answers with an OBJECT keyed by language name ({ "Hindi": …, "English": … }),
   * so the names are `Object.keys(data)`. Handled defensively for an array too.
   */
  getLanguageOptions(onResponse, onError) {
    return this.apiService.get(`${APIConfig.base}/language`, onResponse, onError);
  }

  /**
   * Save the patient's preferred languages — bridge's updateLanguage: POST to
   * /ppl with the full set as codes, `{ patientId, languageCode: [codes] }`.
   *
   * SENDS A DEDUPED, BOUNDED SET — at most one code per language in the master
   * list — never the raw stored `/ppl` list. Bridge's fixed Language.js now does
   * the same, but its OLD version echoed the stored rows back, and against the
   * doubling backend that made the body grow with the table on every click:
   * 3k rows -> 6k -> 12k, each save slower than the last, observed as "never
   * stopping". The backend replaces properly now, so the bound is no longer
   * protecting against a runaway — but rows written during that period are still
   * stored, so a save from a corrupted patient must stay one small request.
   */
  savePatientLanguages(patientId, languageCode, onResponse, onError) {
    // The request is never built while the switch is off — see
    // LANGUAGE_EDITING_ENABLED. Failing loudly (onError, not onResponse) so a
    // caller can never mistake the no-op for a successful save.
    if (!LANGUAGE_EDITING_ENABLED) {
      if (onError) {
        onError({
          blocked: true,
          message: "Preferred-language editing is turned off in this build.",
        });
      }
      return undefined;
    }

    const url = `${APIConfig.base}/patients/${patientId}/ppl`;

    return this.apiService.post(url, { patientId, languageCode }, onResponse, onError);
  }

  /**
   * The patient's primary-therapy code — { primaryTherapy: int, answer: "int" }.
   * A 200 with no therapy set, a 404, or answer "none" all mean "no condition";
   * the caller maps the code to a name via getTherapyOptions and hides the chip
   * when there is nothing to show.
   */
  getPrimaryTherapy(patientId, onResponse, onError) {
    const url = APIConfig.patientAssessment.primaryTherapy.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The therapy-code master list — { fields: [{ id, text }] }. Patient-agnostic
   * (the assessment's question 1), so it can be fetched once and reused.
   */
  getTherapyOptions(onResponse, onError) {
    return this.apiService.get(
      APIConfig.patientAssessment.therapyQuestions,
      onResponse,
      onError
    );
  }

  /**
   * ALL of the patient's documents, grouped by type — bridge's getAllDocuments
   * (getByPatientDoc). NOT appointment-scoped: this is every document the patient
   * has, which is what the header's "View all" shows. Responds
   * [{ type, count, all: [document] }].
   */
  getPatientDocuments(patientId, onResponse, onError) {
    const url = APIConfig.documentApis.getByPatientDoc.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Move one document into another folder (bridge's DocumentService.updateType).
   *
   * `type` is the NUMERIC folder id, straight from DOC_TYPE_OPTIONS — not a
   * canonicalType()'d one. That fold is a read-path guarantee for the legacy
   * prescriptions type, and the server does it again on save
   * (document-service controllers/document.go:421-423), so folding here would
   * only hide which folder the operator actually picked.
   *
   * Responds with the whole updated document, including the `typeTitle` the
   * server re-derives.
   */
  updateDocumentType(documentId, type, onResponse, onError) {
    const url = APIConfig.documentApis.updateType.format(documentId);

    return this.apiService.patch(url, { type }, onResponse, onError);
  }

  /**
   * Rename / re-describe one document (bridge's updateNameAndDescription).
   *
   * BOTH FIELDS ARE ALWAYS SENT, even when only one was edited: the handler
   * assigns each unconditionally (controllers/document.go:343-344), so leaving
   * `description` out would blank a description the operator never touched.
   * That is why this takes the pair rather than a partial patch object.
   */
  updateDocumentNameAndDescription(documentId, { title, description }, onResponse, onError) {
    const url = APIConfig.documentApis.updateName.format(documentId);

    return this.apiService.patch(
      url,
      { title: String(title || ""), description: String(description || "") },
      onResponse,
      onError
    );
  }

  /**
   * Mint + cache the doc token the file URLs need (bridge's saveNewDocToken).
   * Stored under the shared DOC_TOKEN_STORAGE_KEY (zy.bridge.doc.token) so
   * buildDocUrl can append it synchronously at click time (a token fetch on
   * click would trip the popup blocker).
   *
   * Only a real token is written: that key is shared, and bridge's legacy
   * documents screen JSON.parses it unguarded, so a 204's `undefined` would
   * break more than this menu. Best-effort: after a failed mint, files open
   * with whatever token the key already held (or none).
   */
  generateDocToken(onResponse, onError) {
    const url = APIConfig.documentApis.tokenGenerate;

    return this.apiService.get(
      url,
      (res) => {
        if (res && res.token) {
          try {
            localStorage.setItem(DOC_TOKEN_STORAGE_KEY, JSON.stringify(res));
          } catch (e) {
            /* storage disabled — files just open untokenised */
          }
        }
        if (onResponse) onResponse(res);
      },
      onError
    );
  }

  /**
   * Upload one document to the PATIENT's own library — the header's
   * "All documents" upload (Figma 6326:6687).
   *
   * Deliberately NOT AppointmentService.uploadDocument: that one hard-codes
   * `title: fileName`, which would silently throw away the title the operator
   * typed and file the document under its file name instead.
   *
   * THE MULTIPART SHAPE, from document-service controllers/document.go:157-176:
   *   file        REQUIRED. 422 without it.
   *   document    REQUIRED, a JSON STRING. Appending the object directly
   *               serialises to "[object Object]" and the server stores an
   *               empty record.
   *   patientId   REQUIRED as a form field TOO, and it is NOT the same field as
   *               the one inside `document`: the form value only chooses the
   *               storage folder, while the row's patient comes solely from the
   *               JSON. They are sent from one argument here so they cannot
   *               drift apart — and `document.patientId` must be a NUMBER and
   *               must be present, because the handler type-switches on it and
   *               panics into a 500 on nil.
   *   fileName    Optional; the server falls back to the file's own name.
   *
   * TWO FIELDS BRIDGE SENDS THAT WE DO NOT, both verified inert rather than
   * assumed so:
   *   patientPhoneNo  never read by the handler at all.
   *   notifyPatient   parsed, then gated behind `userResponse.ID > 0` where
   *                   `userResponse` is a zero-value struct that is never
   *                   assigned — the branch has been unreachable since the
   *                   lookup was refactored out. Sending true notifies nobody.
   * Please do not "restore parity" by adding them back.
   */
  uploadPatientDocument(patientId, file, meta, onResponse, onError) {
    const form = new FormData();
    const pid = Number(patientId);

    form.append("file", file);
    form.append("fileName", meta.fileName);
    form.append(
      "document",
      JSON.stringify({
        patientId: pid,
        title: meta.title,
        description: meta.description || "",
        type: meta.type,
        format: meta.format,
        status: meta.status,
      })
    );
    form.append("patientId", String(pid));

    return this.apiService.postWithFormData(
      APIConfig.documentApis.uploadDoc,
      form,
      onResponse,
      onError
    );
  }
}

export default PatientService;
